import {IconCheck, IconClock, IconX} from "@tabler/icons-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useBalanceStore} from "@/store/useBalance.store";
import {
  getGameBetLimits,
  isBetAmountValid,
  validateBetAmount,
} from "../../../../private/Lottery/Games/DefaultLottery/utils/betLimits";
import useLotteryRecovery from "../../../../private/Lottery/hooks/useLotteryRecovery";
import {ROW_ADJUSTABLE, ROW_BELOW_MIN, ROW_UNAVAILABLE} from "../../../../private/Lottery/utils/lotteryTicketRecovery";
import {transformDrawsToSorteos} from "../../../../private/Lottery/utils/lotteryDataTransformers";
import {getVenezuelanTime, isDrawPassed} from "../../../../private/Lottery/utils/timeUtils";
import styles from "./QuickBuyModal.module.css";

const CLOSE_MS = 150;
const COUNTDOWN_INTERVAL = 1000;
const URGENT_THRESHOLD_MIN = 5;
const LOCAL_ICON_PATH = "/src/assets/images/lottery-icons";

const getNextValidDraw = draws => {
  const sorteos = transformDrawsToSorteos(draws || []);
  for (const s of sorteos) {
    if (!isDrawPassed(s.hora, s.closeTime)) return s;
  }
  return null;
};

const getCountdownMs = closeTime => {
  if (!closeTime) return null;
  const match = closeTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, h, m, s = "0"] = match;
  const now = getVenezuelanTime();
  const close = new Date(now);
  close.setHours(Number(h), Number(m), Number(s), 0);
  const diff = close.getTime() - now.getTime();
  return diff > 0 ? diff : 0;
};

const formatCountdown = ms => {
  if (ms == null || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}min ${String(seconds).padStart(2, "0")}s`;
};

const QuickBuyModal = ({card, game, onClose}) => {
  const {t} = useTranslation();
  const [closing, setClosing] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [success, setSuccess] = useState(false);
  const [countdownMs, setCountdownMs] = useState(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const balance = useBalanceStore(state => state.getBalance);

  // Shared limit-recovery controller (Phase 3). Quick Buy posts a single Animalitos
  // figure ticket to /lottery/ticket — the same in-scope standard lottery endpoint
  // as the boards — so it must attach intent/attempt correlation, reuse the SAME
  // attempt on an uncertain technical retry (the backend idempotency guard then
  // prevents a second provider call / double sale) and surface structured limit
  // messages. `onSettled` fires only on a fully accepted purchase; a limit rejection
  // keeps the amount so the player can apply the available suggestion and confirm a
  // NEW attempt under the SAME intent.
  const rec = useLotteryRecovery({onSettled: () => setSuccess(true)});
  const {isPending} = rec;
  const inReview = rec.recovery?.phase === "review";
  const reviewRow = inReview ? (rec.recovery.rows || [])[0] : null;
  const reviewStatus = reviewRow?.recovery?.status;
  const retryUncertain = !!rec.recovery?.reuseAttempt && !rec.recovery?.blockResubmit;

  const betLimits = useMemo(() => getGameBetLimits(game), [game]);
  const {minBet, maxBet} = betLimits;

  const amountChips = useMemo(
    () => [20, 50, 100, 200, 400].filter(v => isBetAmountValid(v, betLimits)),
    [betLimits]
  );

  const validDraw = useMemo(() => getNextValidDraw(game?.draws), [game?.draws]);

  const drawExpired = useMemo(() => {
    if (!validDraw) return true;
    return countdownMs != null && countdownMs <= 0;
  }, [validDraw, countdownMs]);

  // Countdown timer
  useEffect(() => {
    if (!validDraw?.closeTime) return;
    setCountdownMs(getCountdownMs(validDraw.closeTime));

    const id = setInterval(() => {
      const remaining = getCountdownMs(validDraw.closeTime);
      setCountdownMs(remaining);
      if (remaining <= 0) clearInterval(id);
    }, COUNTDOWN_INTERVAL);

    return () => clearInterval(id);
  }, [validDraw?.closeTime]);

  // Escape key
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") startClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const startClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose(), CLOSE_MS);
  }, [closing, onClose]);

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) startClose();
  };

  const handleAmountChange = e => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setAmount(val);
    setAmountError("");
  };

  const handleChipClick = value => {
    setAmount(String(value));
    setAmountError("");
  };

  const getBetLimitErrorMessage = result => {
    if (!result || result.valid) return null;
    switch (result.reason) {
      case "min":
        return t("qbErrorMinBet", {defaultValue: "Mínimo {{min}} Bs", min: result.minBet});
      case "max":
        return t("qbErrorMaxBet", {defaultValue: "Máximo {{max}} Bs", max: result.maxBet});
      case "step":
        return t("qbErrorBetStep", {defaultValue: "El monto debe ser múltiplo de {{step}}", step: result.step});
      default:
        return null;
    }
  };

  // Live feedback while typing: shown as soon as a non-empty amount breaks a
  // limit, so the user sees why the confirm button is disabled instead of
  // just finding it inert.
  const liveLimitError = useMemo(() => {
    if (!amount) return null;
    return getBetLimitErrorMessage(validateBetAmount(amount, betLimits));
  }, [amount, betLimits, t]);

  const validateAmount = () => {
    const num = Number(amount);
    const result = validateBetAmount(amount, betLimits);
    if (!result.valid) {
      setAmountError(getBetLimitErrorMessage(result) || t("qbErrorAmount", "Ingresa un monto válido"));
      return false;
    }
    if (balance != null && num > balance) {
      setAmountError(t("qbErrorBalance", "Saldo insuficiente"));
      return false;
    }
    return true;
  };

  // Build the single payable figure row keyed by a stable client id so any
  // structured limit issue maps back to exactly this selection (never by number).
  const buildRow = drawId => {
    const meta = {drawId, selectionType: "figure", signId: String(card.number), number: null};
    const rowId = rec.resolveRowId(meta);
    const amt = Number(amount) || 0;
    return {
      rowId,
      drawId,
      selectionType: "figure",
      number: null,
      signId: String(card.number),
      amountBs: amt,
      originalAmountBs: amt,
      isGroupBetVisual: false,
    };
  };

  const handleConfirm = () => {
    if (isPending || drawExpired) return;
    if (rec.recovery?.blockResubmit) return;

    // Re-check draw validity at confirmation time
    const freshDraw = getNextValidDraw(game?.draws);
    if (!freshDraw) {
      setAmountError(t("qbErrorDrawClosed", "Sorteo cerrado"));
      return;
    }

    // During review the reviewed draft is authoritative (it already carries any
    // applied suggestion or, for an uncertain retry, the unchanged amounts so the
    // SAME attempt id is replayed). Only a fresh (non-review) purchase validates and
    // rebuilds the row from the current input.
    if (inReview) {
      rec.submit(rec.recovery.rows, {currency: "VES", isAnimalGame: true});
      return;
    }
    if (!validateAmount()) return;
    rec.submit([buildRow(freshDraw.id)], {currency: "VES", isAnimalGame: true});
  };

  // Apply the backend's available suggestion to the reviewed row and keep the
  // (locked) input display in sync so old fichas can't reappear in the total.
  const handleApplySuggestion = () => {
    const suggested = reviewRow?.recovery?.suggestedBs;
    if (suggested == null) return;
    rec.handleUseSuggestion(reviewRow.rowId);
    setAmount(String(suggested));
    setAmountError("");
  };

  // Discard ends the correlation session (a fresh intent) and re-enables editing.
  const handleDiscardRecovery = () => {
    rec.resetAll();
    setAmountError("");
  };

  if (!card || !game) return null;

  const countdownStr = formatCountdown(countdownMs);
  const isUrgent = countdownMs != null && countdownMs > 0 && countdownMs < URGENT_THRESHOLD_MIN * 60 * 1000;
  const numericAmount = Number(amount);
  const canConfirm =
    !drawExpired &&
    !isPending &&
    !rec.recovery?.blockResubmit &&
    (inReview ? rec.reviewCanConfirm : isBetAmountValid(amount, betLimits));

  // ── Success state ──
  if (success) {
    return createPortal(
      <div
        className={`${styles.overlay} ${closing ? styles.closing : ""}`}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.panel}>
          <div className={styles.handleBar} />
          <div className={styles.successWrap}>
            <div className={styles.successIcon}>
              <IconCheck
                size={28}
                stroke={2.5}
              />
            </div>
            <span className={styles.successTitle}>{t("qbSuccessTitle", "¡Compra exitosa!")}</span>
            <span className={styles.successDetail}>
              {card.displayName} #{String(card.number).padStart(2, "0")} — {card.gameName}
            </span>
            <span className={styles.successAmount}>Bs. {numericAmount.toLocaleString("es-VE")}</span>
            <button
              type="button"
              className={styles.successClose}
              onClick={startClose}
            >
              {t("qbClose", "Cerrar")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── Purchase form state ──
  return createPortal(
    <div
      className={`${styles.overlay} ${closing ? styles.closing : ""}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t("qbAriaLabel", "Compra rápida de animalito")}
    >
      <div
        className={styles.panel}
        ref={panelRef}
      >
        <div className={styles.handleBar} />
        <button
          type="button"
          className={styles.closeBtn}
          onClick={startClose}
          aria-label="Close"
        >
          <IconX
            size={16}
            stroke={2}
          />
        </button>

        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroImageWrap}>
            <img
              src={card.imageUrl}
              alt={card.displayName}
              className={styles.heroImage}
              onError={e => {
                e.target.onerror = null;
                e.target.src = `${LOCAL_ICON_PATH}/${card.name}.svg`;
              }}
            />
          </div>
          <div className={styles.heroInfo}>
            <span className={styles.heroNumber}>#{String(card.number).padStart(2, "0")}</span>
            <span className={styles.heroName}>{card.displayName}</span>
            <span className={styles.heroLottery}>{card.gameName}</span>
          </div>
        </div>

        {/* Draw info */}
        <div className={styles.drawRow}>
          <div className={styles.drawLabel}>
            <IconClock
              size={14}
              stroke={2}
            />
            {validDraw ? (
              <>
                <span>{t("qbNextDraw", "Sorteo")}</span>
                <span className={styles.drawTime}>{validDraw.hora}</span>
              </>
            ) : (
              <span className={styles.drawClosed}>{t("qbNoDraws", "Sin sorteos disponibles")}</span>
            )}
          </div>
          {validDraw && countdownStr && (
            <span className={`${styles.countdown} ${isUrgent ? styles.countdownUrgent : ""}`}>{countdownStr}</span>
          )}
          {validDraw && drawExpired && <span className={styles.drawClosed}>{t("qbDrawClosed", "Cerrado")}</span>}
        </div>

        {/* Amount input */}
        <div className={styles.amountSection}>
          <label
            className={styles.amountLabel}
            htmlFor="qb-amount"
          >
            {t("qbAmountLabel", "Monto a apostar")}
          </label>
          <div className={styles.amountInputWrap}>
            <span className={styles.amountCurrency}>Bs.</span>
            <input
              ref={inputRef}
              id="qb-amount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={styles.amountInput}
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              disabled={drawExpired || !!rec.recovery}
              autoComplete="off"
            />
          </div>
          {(amountError || liveLimitError) && (
            <span className={styles.amountError}>{amountError || liveLimitError}</span>
          )}
          <span className={styles.amountRange}>
            Min: {minBet} Bs — Max: {maxBet} Bs
          </span>
        </div>

        {/* Quick chips */}
        <div className={styles.chipsRow}>
          {amountChips.map(v => (
            <button
              key={v}
              type="button"
              className={`${styles.chip} ${numericAmount === v ? styles.chipActive : ""}`}
              onClick={() => handleChipClick(v)}
              disabled={drawExpired || !!rec.recovery}
            >
              {v.toLocaleString("es-VE")}
            </button>
          ))}
        </div>

        {/* Limit-recovery review (Phase 3): structured message + safe actions only */}
        {rec.recovery && (
          <div className={styles.recoveryBanner} role="alert" aria-live="assertive">
            <span className={styles.recoveryText}>{rec.recovery.message}</span>
            {reviewStatus === ROW_ADJUSTABLE && reviewRow?.recovery?.suggestedBs != null && !rec.recovery.uncertain && (
              <button type="button" className={styles.recoveryAction} onClick={handleApplySuggestion}>
                {`Usar disponible Bs ${Number(reviewRow.recovery.suggestedBs).toLocaleString("es-VE")}`}
              </button>
            )}
            {(reviewStatus === ROW_UNAVAILABLE || reviewStatus === ROW_BELOW_MIN) && (
              <span className={styles.recoveryNote}>
                {reviewStatus === ROW_BELOW_MIN
                  ? `Quedan Bs ${Number(reviewRow.recovery.availableBs ?? 0).toLocaleString("es-VE")}, menos que el mínimo de apuesta.`
                  : "Este animalito quedó sin disponibilidad en este sorteo."}
              </span>
            )}
            {retryUncertain && (
              <span className={styles.recoveryNote}>Reintenta la misma jugada sin cambiar el monto.</span>
            )}
            <button
              type="button"
              className={styles.recoveryDiscard}
              onClick={() => (rec.recovery.blockResubmit ? startClose() : handleDiscardRecovery())}
            >
              {rec.recovery.blockResubmit ? t("qbClose", "Cerrar") : t("qbDiscardChange", "Descartar y cambiar monto")}
            </button>
          </div>
        )}

        {/* Confirm button */}
        <button
          type="button"
          className={styles.confirmBtn}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {isPending ? (
            <span className={styles.btnSpinner} />
          ) : (
            <>
              {t("qbConfirm", "Confirmar compra")}
              {numericAmount > 0 && ` — Bs. ${numericAmount.toLocaleString("es-VE")}`}
            </>
          )}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default QuickBuyModal;
